import {
  type ContentfulClientApi,
  createClient,
  type CreateClientParams,
} from 'contentful'

import { sentryLogging } from 'sentry-utils/logging'
import type { Entry, EntryCollection } from '../types'

export type ContentfulEntriesQuery = {
  content_type?: string
  [key: string]: unknown
}

async function retry<T>(call: () => Promise<T>, retries: number): Promise<T> {
  try {
    return await call()
  } catch (err) {
    if (retries - 1 > 0) {
      return retry(call, retries - 1)
    }
    throw err
  }
}

/**
 * A client wrapper for interacting with the Contentful API.
 * Provides methods to fetch entries, single entry, and mapped entries
 * with error handling and logging capabilities.
 *
 * @remarks
 * This class uses the Contentful SDK to interact with the Contentful API.
 * It includes error handling with Sentry logging for better observability.
 *
 * @example
 * ```typescript
 * const client = new ContentfulClient({
 *   space: 'your-space-id',
 *   accessToken: 'your-access-token',
 * });
 *
 * const entries = await client.getEntries<MyContentType>({
 *   content_type: 'myContentType',
 * });
 * ```
 */
export class ContentfulClient {
  private readonly space: string
  private client: ContentfulClientApi

  constructor(config: CreateClientParams) {
    this.client = createClient(config)
    this.space = config.space
  }

  /**
   * Asserts the client configuration by attempting to fetch the space.
   * If the configuration is invalid, it calls the provided error callback.
   *
   * @param onError - Callback function to handle errors.
   */
  async assertClient(onError: (reason: unknown) => void) {
    // Check if configuration is valid.
    const space = await retry(() => this.client.getSpace(), 3).catch(reason => {
      onError(reason)
    })

    if (!space) {
      onError(`Could not get Contentful space with "${this.space}" id.`)
    }
  }

  /**
   * Fetches raw entries from Contentful with system properties.
   * If an error occurs, it logs the error using Sentry.
   *
   * @template T - The type of the entries to fetch. Check Entry type in the autogenerated types
   * for your content_type in the query. Like IPagePackage for pagePackage content type. Otherwise
   * any or unkown can be used.
   * @param query - Optional query parameters to filter the entries.
   * @returns A promise that resolves to an EntryCollection or null if an error occurs.
   */
  async getEntriesWithSys<T>(
    query?: ContentfulEntriesQuery
  ): Promise<EntryCollection<T> | null> {
    try {
      return await retry(() => this.client.getEntries<T>(query), 3)
    } catch (error) {
      const contentType = query?.content_type || 'unknown'
      sentryLogging({
        team: 'team-frontend-infrastructure',
        error: new Error(
          `Contentful: Error when fetching entries: ${contentType}`
        ),
        extras: { error, query },
      })
      return null
    }
  }

  /**
   * Fetches a single raw entry from Contentful with system properties.
   * If an error occurs, it logs the error using Sentry.
   *
   * @template T - The type of the entries to fetch. Check Entry type in the autogenerated types
   * for your content_type in the query. Like IPagePackage for pagePackage content type. Otherwise
   * any or unkown can be used.
   * @param id - The ID of the entry to fetch.
   * @param query - Optional query parameters to filter the entry.
   * @returns A promise that resolves to an Entry or null if an error occurs.
   */
  async getEntryWithSys<T>(
    id: string,
    query?: ContentfulEntriesQuery
  ): Promise<Entry<T> | null> {
    try {
      return await retry(() => this.client.getEntry<T>(id, query), 3)
    } catch {
      sentryLogging({
        team: 'team-frontend-infrastructure',
        error: new Error(`Contentful: Error when fetching entry ${id}`),
        extras: { query },
      })
      return null
    }
  }

  /**
   * Fetches entries from Contentful and returns only the fields.
   * If an error occurs, it logs the error using Sentry.
   *
   * @template T - The type of the entry fields to return. Check Entry type in the autogenerated types
   * for your content_type in the query. Like IPagePackage for pagePackage content type. Otherwise
   * any, unkown can be used.
   * @param query - Optional query parameters to filter the entries.
   * @returns A promise that resolves to an array of mapped entries or an empty array if an error occurs.
   */
  async getEntries<T>(query?: ContentfulEntriesQuery): Promise<T[]> {
    const entries = await this.getEntriesWithSys<T>(query)

    if (entries?.errors) {
      sentryLogging({
        team: 'team-frontend-infrastructure',
        error: new Error('Contentful: Error when fetching entries'),
        extras: { errors: entries.errors, query },
      })
    }

    return entries?.items?.map(item => item.fields) ?? []
  }

  /**
   * Fetches entries from Contentful with system properties and maps them to the specified type.
   * If an error occurs, it logs the error using Sentry.
   *
   * @template T - The type of the entries to return. Check Entry type in the autogenerated types
   * for your content_type in the query. Like IPagePackage for pagePackage content type. Otherwise
   * any, unkown can be used.
   * @template I - The custom type to return based on `T`
   * @param query - Optional query parameters to filter the entries.
   * @returns A promise that resolves to an array of mapped entries or an empty array if an error occurs.
   */
  async getMappedEntries<T, I extends Partial<T>>(
    query?: ContentfulEntriesQuery
  ): Promise<I[]> {
    const entries = await this.getEntriesWithSys<T>(query)

    if (entries?.errors) {
      sentryLogging({
        team: 'team-frontend-infrastructure',
        error: new Error('Contentful: Error when fetching entries'),
        extras: { errors: entries.errors, query },
      })
    }

    return entries?.items?.map(item => item.fields as unknown as I) ?? []
  }

  /**
   * Fetches a single entry from Contentful and returns only the fields.
   * If an error occurs, it logs the error using Sentry.
   *
   * @template T - The type of the entry fields to return. Check Entry type in the autogenerated types
   * for your content_type in the query. Like IPagePackage for pagePackage content type. Otherwise
   * any, unkown can be used.
   * @param id - The ID of the entry to fetch.
   * @param query - Optional query parameters to filter the entry.
   * @returns A promise that resolves to a mapped entry or null if an error occurs.
   */
  async getEntry<T>(id: string, query?: ContentfulEntriesQuery) {
    const entry = await this.getEntryWithSys<T>(id, query)

    return entry?.fields
  }
}
